import { DataService } from '../../dataService';
import { AxiosPromise } from 'axios';
import { TransactionTeam } from '../models/TransactionTeamModels';
import { TransactionRole } from '../models/AIMModels';
import {
  CancelTransactionTeamParams,
  TransactionTeamLeadParams,
  TransactionTeamTransactionRoleParams
} from '../models/APIModels';
import { ENV } from '../envUtils';

/**
 * Factory function that constructs a transaction team controller with HTTP methods for CRUD, lifecycle transitions, lead management, priority changes, and Jarvis data integration.
 *
 * Takes a DataService instance and returns methods that wrap either GET/list fetches or POST/PUT/DELETE calls sending form data and a route.
 */
export const transactionTeamController = (api: DataService) => ({
  getTransactionRoles: (): AxiosPromise<TransactionRole[]> => api.get({ route: [`${ENV}/transactionroles`] }),
  getTransactionRole: ({
    aimTransactionRole
  }: Pick<TransactionRole, 'aimTransactionRole'>): AxiosPromise<TransactionRole> =>
    api.get({ route: [`${ENV}/transactionrole/${aimTransactionRole}`] }),
  getTransactionTeams: (): AxiosPromise<TransactionTeam[]> => api.get({ route: [`${ENV}/transactionteams`] }),
  newTransactionTeam: (payload: TransactionTeam): AxiosPromise<void> =>
    api.post({
      route: [`${ENV}/transactionteam/new`],
      formData: payload
    }),
  getTransactionTeam: ({
    aimTransactionTeam
  }: Pick<TransactionTeam, 'aimTransactionTeam'>): AxiosPromise<TransactionTeam> =>
    api.get({ route: [`${ENV}/transactionteam/${aimTransactionTeam}`] }),
  updateTransactionTeam: (payload: TransactionTeam): AxiosPromise<void> =>
    api.put({
      route: [`${ENV}/transactionteam/${payload.aimTransactionTeam}`],
      formData: payload
    }),
  deleteTransactionTeam: ({
    aimTransactionTeam
  }: Pick<TransactionTeam, 'aimTransactionTeam'>): AxiosPromise<void> =>
    api.delete({
      route: [`${ENV}/transactionteam/${aimTransactionTeam}`]
    }),
  cancelTransactionTeam: (payload: CancelTransactionTeamParams): AxiosPromise<void> =>
    api.post({
      route: [`${ENV}/transactionteam/${payload.aimTransactionTeam}/cancel`],
      formData: { reason: payload.reason }
    }),
  submitTransactionTeam: ({
    aimTransactionTeam
  }: Pick<TransactionTeam, 'aimTransactionTeam'>): AxiosPromise<void> =>
    api.post({
      route: [`${ENV}/transactionteam/${aimTransactionTeam}/submit`]
    }),
  revertTransactionTeam: ({
    aimTransactionTeam
  }: Pick<TransactionTeam, 'aimTransactionTeam'>): AxiosPromise<void> =>
    api.post({
      route: [`${ENV}/transactionteam/${aimTransactionTeam}/revert`]
    }),
  formTransactionTeam: ({
    aimTransactionTeam
  }: Pick<TransactionTeam, 'aimTransactionTeam'>): AxiosPromise<void> =>
    api.post({
      route: [`${ENV}/transactionteam/${aimTransactionTeam}/form`]
    }),
  archiveTransactionTeam: ({
    aimTransactionTeam
  }: Pick<TransactionTeam, 'aimTransactionTeam'>): AxiosPromise<void> =>
    api.post({
      route: [`${ENV}/transactionteam/${aimTransactionTeam}/archive`]
    }),
  proposeTransactionTeamLead: ({
    aimTransactionTeam,
    role,
    aimEmployee
  }: TransactionTeamLeadParams): AxiosPromise<void> =>
    api.post({
      route: [`${ENV}/transactionteam/${aimTransactionTeam}/lead/${role}/propose`],
      formData: { aimEmployee: aimEmployee }
    }),
  approveTransactionTeamLead: ({
    aimTransactionTeam,
    role
  }: TransactionTeamTransactionRoleParams): AxiosPromise<void> =>
    api.post({
      route: [
        `${ENV}/transactionteam/${aimTransactionTeam?.aimTransactionTeam}/lead/${role?.aimTransactionRole}/approve`
      ]
    }),
  replaceTransactionTeamLead: ({
    aimTransactionTeam,
    role,
    aimEmployee
  }: TransactionTeamLeadParams): AxiosPromise<void> =>
    api.post({
      route: [
        `${ENV}/transactionteam/${aimTransactionTeam?.aimTransactionTeam}/lead/${role?.aimTransactionRole}/replace`
      ],
      formData: { aimEmployee: aimEmployee }
    }),
  deleteTransactionTeamLead: ({
    aimTransactionTeam,
    role
  }: TransactionTeamTransactionRoleParams): AxiosPromise<void> =>
    api.delete({
      route: [`${ENV}/transactionteam/${aimTransactionTeam}/lead/${role}`]
    }),
  raisePriority: ({ aimTransactionTeam }: Pick<TransactionTeam, 'aimTransactionTeam'>): AxiosPromise<void> =>
    api.get({ route: [`${ENV}/transactionteam/${aimTransactionTeam}/priority/raise`] }),
  lowerPriority: ({ aimTransactionTeam }: Pick<TransactionTeam, 'aimTransactionTeam'>): AxiosPromise<void> =>
    api.get({ route: [`${ENV}/transactionteam/${aimTransactionTeam}/priority/lower`] }),
  addJarvisData: ({
    jarvisId,
    jarvisName,
    aimTransactionTeam
  }: Partial<TransactionTeam>): AxiosPromise<TransactionTeam[]> =>
    api.post({
      route: [`${ENV}/transactionteam/${aimTransactionTeam}/jarvis`],
      formData: { jarvisId, jarvisName }
    })
});
